import React from "react";
import { TextField, InputAdornment } from "@mui/material";
import { Controller } from "react-hook-form";

interface TextInputProps {
  name: string;
  label: string;
  control: any;
  errors: any;
  type?: string;
  multiline?: boolean;
  isDisabled?: boolean;
  onChange?: (event: any) => void;
  showEuro?: boolean;
  showPercentage?: boolean;
  onlyNumbers?: boolean;
  onlyFourDecimal?: boolean;
  showInYears?: boolean;
  percentageWithTwoDecimals?: boolean;
  className?: string;
  isProjectName?: boolean;
  showEuroWithoutMillion?: boolean; 
  isComment?: boolean | { enabled: boolean; maxLength: number };
}

const TextInput: React.FC<TextInputProps> = ({
  name,
  label, 
  control,
  errors,
  className,
  type = "text",
  multiline = false,
  onChange,
  isDisabled = false,
  showEuro = false,
  showPercentage = false,
  onlyNumbers = false,
  onlyFourDecimal = false,
  showInYears = false,
  percentageWithTwoDecimals = false,
  isProjectName = false,
  isComment = false,
  showEuroWithoutMillion = false,
  
}) => {
  const projectNameRegex = /^[a-zA-Z0-9 _-]*$/; 
  const commentRegex = /^[a-zA-Z0-9\s.,!?;:()'-]*$/;

  const isCommentEnabled = typeof isComment === "object" ? isComment.enabled : isComment;
  const maxCommentLength = typeof isComment === "object" ? isComment.maxLength : 2000;

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    
    let value = e.target.value;

    // Comment validation
    if (isCommentEnabled) {
      // if (!commentRegex.test(value)) {
      //   // value = value.replace(/[^a-zA-Z0-9\s.,!?;:()'\-*\u2022]/g, "");
      //    value = value.replace(/[^\w\s.,!?;:()'"@#%^&*\-+=~[\]{}<>|\\/•\u2022]/g, "");
      // }
      if (value.length > maxCommentLength) {
        value = value.substring(0, maxCommentLength);
      }
    }

    // Project Name validation
    if (isProjectName) {
      // Only allow alphanumeric, spaces, hyphens, and underscores
      if (!projectNameRegex.test(value)) {
        value = value.replace(/[^a-zA-Z0-9 _-]/g, "");
      }
      // Limit to 255 characters
      if (value.length > 255) {
        value = value.substring(0, 255);
      }
    }

    //  For 'percentageWithTwoDecimals' - Max 100, Min 0, Exactly 2 decimal places
    if (percentageWithTwoDecimals) {
      value = value.replace(/[^0-9.]/g, "");

      const decimalCount = (value.match(/\./g) || []).length;
      if (decimalCount > 1) {
        value = value.slice(0, value.lastIndexOf("."));
      }

      const [integerPart = "", decimalPart = ""] = value.split(".");

      // Ensure two decimal places
      if (decimalPart.length > 2) {
        value = `${integerPart}.${decimalPart.slice(0, 2)}`;
      }

      // Ensure valid number within range (0 to 100)
      const numValue = parseFloat(value);
      if (value === "") {
        e.target.value = "";
        if (onChange) onChange(e);
        return;
      }
      if (isNaN(numValue) || numValue < 0) return;
      if (numValue > 100) value = "100";
    }

    //  For 'onlyFourDecimal'
    if (onlyFourDecimal) {
      value = value.replace(/[^0-9.]/g, "");
      const decimalCount = (value.match(/\./g) || []).length;
      if (decimalCount > 1) {
        value = value.slice(0, value.lastIndexOf("."));
      }
    
      const [integerPart = "", decimalPart = ""] = value.split(".");
    
      if (decimalPart.length > 4) {
        value = `${integerPart}.${decimalPart.slice(0, 4)}`;
      }
    
      const numValue = parseFloat(value);
      if (value === "") {
        e.target.value = "";
        if (onChange) onChange(e);
        return;
      }
      if (isNaN(numValue) || numValue < 0) return;
      if (numValue > 10000000) value = "10000000";
    }    

    //  For 'onlyNumbers'
    if (onlyNumbers) {
      value = value.replace(/[^0-9.]/g, "");
      const decimalCount = (value.match(/\./g) || []).length;
      if (decimalCount > 1) {
        value = value.slice(0, value.lastIndexOf("."));
      }
    }

    //  For 'showInYears'
    if (showInYears) {
      value = value.replace(/[^0-9.]/g, "");
      const decimalCount = (value.match(/\./g) || []).length;
      if (decimalCount > 1) {
        value = value.slice(0, value.lastIndexOf("."));
      }

      const [integerPart = "", decimalPart = ""] = value.split(".");
      if (decimalPart.length > 1) {
        value = `${integerPart}.${decimalPart.slice(0, 1)}`;
      }

      const numValue = parseFloat(value);
      if (value === "") {
        e.target.value = "";
        if (onChange) onChange(e);
        return;
      }
      if (isNaN(numValue) || numValue < 0) return;
      if (numValue > 10) value = "10";
    }

    e.target.value = value;

    if (onChange) {
      onChange(e);
    }
  };

  return (
    <Controller
      name={name}
      control={control}
      defaultValue=""
      render={({ field }) => (
        <TextField
          {...field}
          disabled={isDisabled}
          label={label}
          className={className}
          fullWidth
          type={type}
          multiline={multiline}
          error={!!errors[name]}
          helperText={errors[name]?.message}
          variant="outlined"
          onChange={(e) => {
            handleChange(e);
            const processedValue = e.target.value === "" ? null : e.target.value;
            field.onChange(processedValue);
          }}
          InputProps={{
            endAdornment: showEuro ? (
              <InputAdornment position="end">M€</InputAdornment>
            ) : showPercentage || percentageWithTwoDecimals ? (
              <InputAdornment position="end">%</InputAdornment>
            ) : showInYears || showInYears ? (
              <InputAdornment position="end">Years</InputAdornment>
            ): showEuroWithoutMillion || showEuroWithoutMillion ? (
              <InputAdornment position="end">€</InputAdornment>
            ) : null,
          }}
          inputProps={{
            inputMode:
              onlyNumbers || onlyFourDecimal || showInYears || percentageWithTwoDecimals
                ? "decimal"
                : undefined,
            maxLength: isProjectName
              ? 255
              : isComment
              ? 2000
              : undefined,
          }}
          sx={{
            ...(isCommentEnabled && {
              // '& .MuiInputBase-root': {
              //   height: '100px',
              //   padding: '8px',
              // },
              // '& .project-exception-input': {
              //   height: '200px!important',
              //   padding: '8px',
              // },
           
            
              '& .MuiInputBase-inputMultiline': {
                height: '100% !important',
                overflowY: 'auto !important',
                padding: 0,
                lineHeight: '1.5',
              },
              '& .MuiInputAdornment-root': {
                marginLeft: '8px',
              },
            }),
          }}
        />
      )}
    />
  );
};

export default TextInput;
